import React, { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  Autocomplete,
  TextField,
  Checkbox,
  ListItemText
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../hooks';
import { fetchExamFullNames } from '../../slices/academicClassElementSlice';
import { useLocation } from 'react-router-dom';

interface Props {
  name: string;
  oldValue?: string | string[] | null;
  error?: string | null;
  size?: 'small' | 'medium';
  multiple?: boolean;
}

const ExamNameSelect: React.FC<Props> = ({
  name,
  size = 'medium',
  oldValue,
  error,
  multiple = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const { setValue, trigger } = useFormContext();

  const [resultData, setResultData] = useState<string[]>([]);
  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const [selectedValues, setSelectedValues] = useState<string[]>([]);

  const allLabel = 'All';
  const optionsWithAll = multiple ? [allLabel, ...resultData] : resultData;

  // Fetch data
  const fetchData = useCallback(async () => {
    const dispatchedData = await dispatch(fetchExamFullNames());
    if (
      dispatchedData.meta.requestStatus === 'fulfilled' &&
      dispatchedData.payload?.data
    ) {
      setResultData(dispatchedData.payload.data);
    }
  }, [dispatch]);

  // Sync form values
  useEffect(() => {
    if (multiple) {
      const valuesToSet = selectedValues.includes(allLabel)
        ? resultData
        : selectedValues;
      setValue(name, valuesToSet);
      trigger(name);
    } else {
      setValue(name, selectedValue);
      trigger(name);
    }
  }, [selectedValue, selectedValues, setValue, trigger, multiple, name, resultData]);

  // Apply old value when component mounts
  useEffect(() => {
    if (oldValue) {
      if (multiple && Array.isArray(oldValue)) {
        const filtered = resultData.filter((val) => oldValue.includes(val));
        const isAllSelected = filtered.length === resultData.length;
        setSelectedValues(isAllSelected ? [allLabel, ...filtered] : filtered);
        setValue(name, filtered);
      } else if (!multiple && typeof oldValue === 'string') {
        const match = resultData.find((val) => val === oldValue) || null;
        setSelectedValue(match);
        setValue(name, match);
      }
    }
  }, [oldValue, resultData, setValue, multiple, name]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Autocomplete
      disabled={pathname === '/marking-schemes/update-marking-scheme'}
      size={size}
      fullWidth
      multiple={multiple}
      value={multiple ? selectedValues : selectedValue}
      onChange={(event, newValue) => {
        if (multiple) {
          const newSelected = newValue as string[];

          // If "All" is selected
          if (newSelected.includes(allLabel)) {
            // If already selected -> deselect all
            if (selectedValues.includes(allLabel)) {
              setSelectedValues([]);
            } else {
              setSelectedValues([allLabel, ...resultData]);
            }
          } else {
            // Check if all values are selected
            const filtered = newSelected.filter((val) => val !== allLabel);
            if (filtered.length === resultData.length) {
              setSelectedValues([allLabel, ...resultData]);
            } else {
              setSelectedValues(filtered);
            }
          }
        } else {
          setSelectedValue(newValue as string);
        }
      }}
      options={optionsWithAll}
      getOptionLabel={(option) => option}
      isOptionEqualToValue={(option, value) => option === value}
      disableCloseOnSelect={multiple}
      renderOption={(props, option, { selected }) => {
        const { key, ...restProps } = props;
        return (
          <li key={key} {...restProps}>
            {multiple && (
              <Checkbox
                style={{ marginRight: 8 }}
                checked={selected}
              />
            )}
            <ListItemText primary={option} />
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t('select exam')}
          error={!!error}
          helperText={error || ''}
        />
      )}
    />
  );
};

export default ExamNameSelect;
